import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { AuditLogEntry } from '@tt/shared';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { useAuditLogs } from '@/lib/admin-queries';
import { describeError } from '@/lib/errors';
import { AdminPage, DataTable, Pagination } from './admin-ui';

const ENTITY_TYPES = ['user', 'group', 'project', 'activity', 'organization', 'time_entry'];

/** Read-only trail of administrative changes, with the before/after of each one. */
export function AdminAuditLogPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/admin/audit-log' });
  const page = search.page ?? 1;

  const logs = useAuditLogs({
    action: search.action || undefined,
    entityType: search.entityType || undefined,
    from: search.from || undefined,
    to: search.to || undefined,
    page,
    pageSize: 25,
  });

  const setSearch = (next: Record<string, unknown>) =>
    void navigate({
      to: '/admin/audit-log',
      search: { ...search, page: 1, ...next },
      replace: true,
    });

  return (
    <AdminPage title={t('admin.audit.title')} description={t('admin.audit.subtitle')}>
      <div className="flex flex-wrap items-end gap-2">
        <NativeSelect
          aria-label={t('admin.audit.filterEntity')}
          value={search.entityType ?? ''}
          onChange={(e) => setSearch({ entityType: e.target.value || undefined })}
          className="w-44"
        >
          <option value="">{t('admin.audit.allEntities')}</option>
          {ENTITY_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </NativeSelect>
        <Input
          type="search"
          defaultValue={search.action ?? ''}
          onChange={(e) => setSearch({ action: e.target.value || undefined })}
          placeholder={t('admin.audit.filterAction')}
          aria-label={t('admin.audit.filterAction')}
          className="max-w-xs"
        />
        <Input
          type="date"
          value={search.from ?? ''}
          onChange={(e) => setSearch({ from: e.target.value || undefined })}
          aria-label={t('admin.org.from')}
          className="w-40"
        />
        <Input
          type="date"
          value={search.to ?? ''}
          onChange={(e) => setSearch({ to: e.target.value || undefined })}
          aria-label={t('admin.org.to')}
          className="w-40"
        />
      </div>

      <DataTable
        caption={t('admin.audit.title')}
        isPending={logs.isPending}
        error={logs.isError ? describeError(logs.error, t) : undefined}
        empty={t('admin.audit.none')}
        rows={logs.data?.data ?? []}
        rowKey={(row) => row.id}
        footer={
          <Pagination
            page={page}
            totalPages={logs.data?.meta.totalPages ?? 1}
            total={logs.data?.meta.total ?? 0}
            onChange={(next) =>
              void navigate({ to: '/admin/audit-log', search: { ...search, page: next } })
            }
          />
        }
        columns={[
          {
            key: 'when',
            header: t('admin.audit.when'),
            cell: (row) => (
              <time dateTime={row.createdAt} className="text-xs tabular-nums">
                {row.createdAt.slice(0, 16).replace('T', ' ')}
              </time>
            ),
          },
          {
            key: 'who',
            header: t('admin.audit.who'),
            cell: (row) => row.actor?.name ?? t('admin.audit.system'),
          },
          {
            key: 'what',
            header: t('admin.audit.what'),
            cell: (row) => <code className="text-xs">{row.action}</code>,
          },
          {
            key: 'target',
            header: t('admin.audit.target'),
            cell: (row) => row.targetUser?.name ?? row.entityType,
          },
          {
            key: 'details',
            header: t('admin.audit.details'),
            cell: (row) => <Diff row={row} />,
          },
        ]}
      />
    </AdminPage>
  );
}

/** Collapsed by default: the list stays scannable and the detail is one click away. */
function Diff({ row }: { row: AuditLogEntry }) {
  const { t } = useTranslation();
  if (!row.before && !row.after) return <span className="text-muted-foreground">—</span>;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground">{t('admin.audit.details')}</summary>
      <div className="mt-1 grid gap-2 sm:grid-cols-2">
        {row.before ? (
          <div>
            <p className="font-medium">{t('admin.audit.before')}</p>
            <pre className="overflow-x-auto rounded bg-muted p-2">
              {JSON.stringify(row.before, null, 2)}
            </pre>
          </div>
        ) : null}
        {row.after ? (
          <div>
            <p className="font-medium">{t('admin.audit.after')}</p>
            <pre className="overflow-x-auto rounded bg-muted p-2">
              {JSON.stringify(row.after, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>
    </details>
  );
}
